import { useState, useEffect } from 'react';
import { intelligenceApi, type SavedInsight } from '../services/api';
import { SparklesIcon, ChevronDownIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useAppStore } from '../stores/appStore';
import { AiCreditsModal } from '../components/modals/AiCreditsModal';

interface Props {
  initiativeId: string;
}

type PulseValue = 'on_track' | 'at_risk' | 'blocked' | 'stale' | 'on_hold';

const PULSE_CONFIG: Record<PulseValue, { label: string; subtitle: string; dot: string; badge: string; text: string }> = {
  on_track: {
    label: 'On Track',
    subtitle: 'No significant risks detected',
    dot: 'bg-green-500',
    badge: 'bg-green-50 border-green-200',
    text: 'text-green-700',
  },
  at_risk: {
    label: 'At Risk',
    subtitle: 'Risks identified that need attention',
    dot: 'bg-amber-500',
    badge: 'bg-amber-50 border-amber-200',
    text: 'text-amber-700',
  },
  blocked: {
    label: 'Blocked',
    subtitle: 'Blocked by dependencies or open issues',
    dot: 'bg-red-500',
    badge: 'bg-red-50 border-red-200',
    text: 'text-red-700',
  },
  stale: {
    label: 'Stale',
    subtitle: 'No meaningful activity in 30+ days',
    dot: 'bg-slate-500',
    badge: 'bg-slate-100 border-slate-300',
    text: 'text-slate-700',
  },
  on_hold: {
    label: 'On Hold',
    subtitle: 'Appears paused or deprioritized',
    dot: 'bg-slate-400',
    badge: 'bg-slate-50 border-slate-200',
    text: 'text-slate-600',
  },
};

const SEVERITY_LABEL: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function CopyButton({ getText }: { getText: () => string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(getText()).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-1 text-xs text-text-tertiary hover:text-text-secondary transition-colors"
    >
      {copied
        ? <span className="text-xs text-green-600 font-medium">Copied</span>
        : <ClipboardDocumentIcon className="w-3.5 h-3.5" />
      }
    </button>
  );
}

function Section({
  label,
  getCopyText,
  children,
}: {
  label: string;
  getCopyText?: () => string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-border-subtle rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle bg-surface-subtle">
        <span className="text-xs font-medium text-text-secondary">{label}</span>
        {getCopyText && <CopyButton getText={getCopyText} />}
      </div>
      <div className="px-3 py-2.5">
        {children}
      </div>
    </div>
  );
}

function InsightBody({ analysis }: { analysis: SavedInsight['analysis'] }) {
  return (
    <div className="space-y-3">
      {/* Headline */}
      {analysis.headline && (
        <p className="text-sm font-medium text-text-primary leading-snug">
          {analysis.headline}
        </p>
      )}

      {/* 1. At-a-glance */}
      {analysis.summary && (
        <Section label="At-a-glance">
          <p className="text-xs text-text-primary leading-relaxed line-clamp-3">
            {analysis.summary}
          </p>
        </Section>
      )}

      {/* 2. Risks & Mitigation */}
      {analysis.risks && analysis.risks.length > 0 && (
        <Section label="Risks & Mitigation">
          <div className="space-y-2">
            {analysis.risks.map((risk, i) => (
              <div key={i} className={i > 0 ? 'border-t border-border-subtle pt-2' : ''}>
                <p className="text-xs text-text-primary">
                  <span className="font-bold">{SEVERITY_LABEL[risk.severity] ?? risk.severity}</span>
                  {' '}{risk.description}
                </p>
                {risk.action && (
                  <p className="text-xs text-text-primary mt-0.5 opacity-70">
                    → {risk.action}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 3. Data signals */}
      {analysis.signals && analysis.signals.length > 0 && (
        <Section label="Data signals">
          <div className="space-y-1.5">
            {analysis.signals.map((signal, i) => (
              <p key={i} className="text-xs text-text-primary">{signal}</p>
            ))}
          </div>
        </Section>
      )}

      {/* 4. PM Suggestions */}
      {analysis.considerations && analysis.considerations.length > 0 && (
        <Section
          label="PM Suggestions"
          getCopyText={() => analysis.considerations.join('\n')}
        >
          <div className="space-y-1.5">
            {analysis.considerations.map((c, i) => (
              <p key={i} className="text-xs text-text-primary">{c}</p>
            ))}
          </div>
        </Section>
      )}

      {/* 5. User Stories */}
      {analysis.userStories && analysis.userStories.length > 0 && (
        <Section
          label="User Stories"
          getCopyText={() => analysis.userStories.join('\n')}
        >
          <div className="space-y-1.5">
            {analysis.userStories.map((story, i) => (
              <div key={i} className="border border-border-subtle rounded px-2.5 py-1.5">
                <p className="text-xs text-text-primary leading-relaxed">{story}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

export function InitiativeIntelligencePanel({ initiativeId }: Props) {
  const [insights, setInsights] = useState<SavedInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isCreditsModalOpen, setIsCreditsModalOpen] = useState(false);
  const aiProviderLabel = useAppStore((s) => s.aiProviderLabel);
  const aiProviderModel = useAppStore((s) => s.aiProviderModel);
  const aiUsage = useAppStore((s) => s.aiUsage);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { insights: history } = await intelligenceApi.getInsightHistory(initiativeId);
        if (!cancelled) setInsights(history);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load insights');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [initiativeId]);

  const analyze = async () => {
    if (aiUsage && !aiUsage.isCustomMode) {
      const outOfFree = aiUsage.insightAiUsed >= aiUsage.insightAiLimit;
      const outOfPaid = aiUsage.paidAiCredits <= 0;
      if (outOfFree && outOfPaid) { setIsCreditsModalOpen(true); return; }
    }
    setAnalyzing(true);
    setError('');
    try {
      const newInsight = await intelligenceApi.generateInsight(initiativeId);
      setInsights((prev) => [newInsight, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze initiative');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 gap-3 text-text-secondary">
        <svg className="animate-spin w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-sm">Loading insights…</span>
      </div>
    );
  }

  const insightCreditsLeft = aiUsage && !aiUsage.isCustomMode
    ? Math.max(0, (aiUsage.insightAiLimit - aiUsage.insightAiUsed)) + aiUsage.paidAiCredits
    : null;

  const latest = insights[0];
  const history = insights.slice(1);
  const pulse = latest ? ((latest.analysis.pulse || 'on_track') as PulseValue) : null;
  const pulseCfg = pulse ? (PULSE_CONFIG[pulse] ?? PULSE_CONFIG.on_track) : null;

  return (
    <>
      <AiCreditsModal
        isOpen={isCreditsModalOpen}
        onClose={() => setIsCreditsModalOpen(false)}
        creditType="insight"
      />
      <div className="space-y-3">
        {/* Empty state */}
        {insights.length === 0 && !analyzing && (
          <>
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <SparklesIcon className="w-8 h-8 text-amber-300" />
              <div className="text-center">
                <p className="text-sm font-medium text-text-primary">No analysis yet</p>
                <p className="text-xs text-text-secondary mt-1">
                  Analyzes status, dependencies, recent comments, and audit history
                </p>
              </div>
              <button
                onClick={analyze}
                disabled={analyzing}
                className="flex items-center gap-2 px-3 py-1.5 border border-amber-300 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 text-amber-700 text-sm font-medium rounded-md transition-colors"
              >
                <SparklesIcon className="w-4 h-4" />
                Analyze with {aiProviderLabel}
              </button>
            </div>
          </>
        )}

        {/* Analyzing spinner */}
        {analyzing && (
          <div className="flex items-center justify-center py-10 gap-3 text-text-secondary">
            <svg className="animate-spin w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-sm">Analyzing…</span>
          </div>
        )}

        {/* Latest analysis */}
        {latest && !analyzing && (
          <div className="space-y-3">
            {/* Pulse badge + Re-analyze button */}
            <div className="flex items-start justify-between gap-3">
              <div>
                {pulseCfg && (
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${pulseCfg.badge} ${pulseCfg.text}`}>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pulseCfg.dot}`} />
                    {pulseCfg.label}
                  </div>
                )}
                <p className="text-xs text-text-tertiary mt-1">
                  {pulseCfg?.subtitle} · Analyzed {timeAgo(latest.createdAt)} · by{' '}
                  <span title={aiProviderModel || undefined} className={aiProviderModel ? 'cursor-help' : ''}>
                    {aiProviderLabel}
                  </span>
                </p>
              </div>
              <button
                onClick={analyze}
                disabled={analyzing}
                className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 border border-amber-300 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 text-amber-700 text-xs font-medium rounded-md transition-colors"
              >
                <SparklesIcon className="w-3.5 h-3.5" />
                Re-analyze
                {insightCreditsLeft !== null && insightCreditsLeft <= 3 && insightCreditsLeft > 0 && (
                  <span className="text-amber-500 font-normal">· {insightCreditsLeft} left</span>
                )}
              </button>
            </div>

            <div className="h-px bg-border-subtle" />

            <InsightBody analysis={latest.analysis} />
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* History disclosure */}
        {history.length > 0 && (
          <div className="border-t border-border-subtle pt-3 mt-2">
            <button
              type="button"
              onClick={() => setHistoryOpen(!historyOpen)}
              className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
              View {history.length} previous {history.length === 1 ? 'analysis' : 'analyses'}
            </button>
            {historyOpen && (
              <div className="mt-3 space-y-4">
                {history.map((insight) => (
                  <div key={insight.id} className="border border-border-subtle rounded-md p-3 bg-surface-subtle">
                    <p className="text-xs text-text-tertiary mb-2">
                      Analyzed {timeAgo(insight.createdAt)} · {new Date(insight.createdAt).toLocaleString()}
                    </p>
                    <InsightBody analysis={insight.analysis} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
